import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactLoading from "react-loading";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

// Enhanced Components
import MultiModalSignature from "./components/MultiModalSignature";
import ResponsiveLayout from "./components/ResponsiveLayout";
import AdvancedPDFViewer from "./components/AdvancedPDFViewer";

// Services and Utilities
import { apiCaller } from "../../services/apiCaller";
import {
  ariaUtils,
  keyboardShortcuts,
  formAccessibility
} from "../../utils";

// Styles
import "./App.css";
import "./EnhancedSignPage.css";

// Set body background
document.body.style = "background: #F5F6F8";

function SignPage(props) {
  // Core state management
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceId, setInvoiceId] = useState("");
  const [aplMigration, setAplMigration] = useState(false);
  const [searchParams] = useSearchParams();

  // Form data state
  const [formData, setFormData] = useState({
    name: "",
    position: "",
    address: "",
    isChecked: false
  });
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  
  // Enhanced signature state
  const [signatures, setSignatures] = useState([]);
  const [activeSignature, setActiveSignature] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  
  // PDF and layout state
  const [pdfUrl, setPdfUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);
  const [documentProgress, setDocumentProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [signatureFields, setSignatureFields] = useState([]);
  const [completedFields] = useState([]);

  // Refs
  const mainContainerRef = useRef();
  const formRef = useRef();
  const hasLoadedData = useRef(false);
  
  // URL params
  let { id } = useParams();
  let unique_url = searchParams.get("unique_url") || "nothing";  

  let navigate = useNavigate();

  // Mobile detection
  const checkIsMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, [checkIsMobile]);

  // Enhanced form validation
  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.name.trim() || !/^[A-Za-z\s]+$/.test(formData.name.trim())) {
      errors.name = "Name is required and should only contain alphabet characters.";
    }
    
    if (!formData.position.trim() || !/^[A-Za-z\s]+$/.test(formData.position.trim())) {
      errors.position = "Position is required and should only contain alphabet characters.";
    }
    
    if (aplMigration && (!formData.address.trim() || formData.address.length < 5)) {
      errors.address = "Address is required and should be at least 5 characters long.";
    }
    
    if (!formData.isChecked) {
      errors.agreement = "You must agree to the terms and conditions.";
    }
    
    // Signature validation is now handled by checking activeSignature existence
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, aplMigration]);

  // Enhanced signature handling
  const handleSignatureCreated = useCallback((signatureDataUrl) => {
    const newSignature = {
      id: Date.now(),
      dataUrl: signatureDataUrl,
      createdAt: new Date(),
      type: 'user_created'
    };
    
    setSignatures(prev => [...prev, newSignature]);
    setActiveSignature(newSignature);
    setSignatureModalOpen(false);
    
    // Announce to screen readers
    ariaUtils.announce("Signature created successfully", "polite");
  }, []);

  // Signature field management
  const handleSignatureFieldDetected = useCallback((fields) => {
    setSignatureFields(fields);
    ariaUtils.announce(`${fields.length} signature fields detected`, "polite");
  }, []);


  // Document progress tracking
  const handleProgressUpdate = useCallback((progress) => {
    setDocumentProgress(progress);
  }, []);

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  // Enhanced form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      const firstError = Object.keys(formErrors)[0];
      if (firstError && formRef.current) {
        const field = formRef.current.querySelector(`[name="${firstError}"]`);
        if (field) {
          field.focus();
          ariaUtils.announce(formErrors[firstError], "assertive");
        }
      }
      return;
    }

    const confirmSave = await Swal.fire({
      title: "Are you sure?",
      text: "This will submit your signed document.",
      icon: "warning",
      confirmButtonText: 'Yes, Submit',
      showCancelButton: true,
      cancelButtonColor: "red",
    });

    if (confirmSave.isConfirmed) {
      setIsLoading(true);
      
      try {
        if(unique_url === "nothing"){
          const submitData = {
            invoice_id: invoiceId,
            filename: activeSignature?.dataUrl || "",
            pic_name: formData.name,
            pic_position: formData.position,
            address: aplMigration ? formData.address : "",
            signatures: signatures,
            completedFields: completedFields,
            documentProgress: documentProgress
          };
  
          await apiCaller.post(`/digitalsign/${invoiceId}`, submitData);

        }else{
          const submitData = {
            pic_name: formData.name,
            pic_position: formData.position,
            filename: activeSignature?.dataUrl || "",
            unique_url: unique_url,
          };
  
          await apiCaller.post(`/digitalsign-gi`, submitData);
        }
        
        Swal.fire(
          "Success", 
          "Your document has been signed successfully!", 
          "success"
        ).then(() => {
          window.location.href = "https://voffice.co.id";
        });
        
      } catch (error) {
        console.error("Submission error:", error);
        const errorMessage = error.response?.data?.message || "Failed to submit document. Please try again.";
        
        Swal.fire("Error", errorMessage, "error");
        setIsLoading(false);
      }
    }
  }, [validateForm, formErrors, formData, invoiceId, activeSignature, signatures, completedFields, documentProgress, aplMigration, unique_url]);

  // Initialize keyboard shortcuts
  useEffect(() => {
    keyboardShortcuts.init();
    
    // Register shortcuts
    keyboardShortcuts.register('ctrl+s', (e) => {
      e.preventDefault();
      handleSubmit();
    });
    
    keyboardShortcuts.register('ctrl+n', (e) => {
      e.preventDefault();
      setSignatureModalOpen(true);
    });

    return () => {
      keyboardShortcuts.cleanup();
    };
  }, [handleSubmit]);

  // Data loading effect
  useEffect(() => {
    // Prevent duplicate API calls
    if (hasLoadedData.current) {
      return;
    }

    const loadDocumentData = async () => {
      try {
        if (unique_url === "nothing") {
        const response = await apiCaller.get(`/getSign/${id}`);
        hasLoadedData.current = true;
        const data = response.data;
        
        setInvoiceId(data.invoice_id);
        
        // Determine category
        let docCategory = data.product_category; // default
        // Check for APL Migration
        if (data.product_id >= 15074 && data.product_id <= 15076) {
          setAplMigration(true);
        }
        
 
        
        // Set PDF URL using the determined category
        const documentUrl = `${process.env.REACT_APP_AGREEMENT_URL}/${docCategory}/${data.invoice_id}`;
        setPdfUrl(documentUrl);
      }else{
        const response = await apiCaller.get(`/getGISign/${unique_url}`);
        hasLoadedData.current = true;
        const data = response.data;
        const documentUrl = `https://vox.myvios.cloud/activation/generalinfo/pdf/${data.filename}`;
        // const documentUrl = `http://vios.test/activation/generalinfo/pdf/${data.filename}`;
        setPdfUrl(documentUrl);
        }
        
        setIsLoading(false);
        
      } catch (error) {
        console.error("Failed to load document:", error);
        setError("Failed to load document. Please try again.");

        // Only show error dialog once
        if (!hasLoadedData.current) {
          hasLoadedData.current = true;
          Swal.fire("Error", error.response.data.message, "error").then(() => {
            navigate("/");
          });
        }
      }
    };

    if (id && !hasLoadedData.current) {
      loadDocumentData();
    }
  }, [id, navigate, unique_url]);

  // Memoized components for performance
  const pdfViewer = useMemo(() => (
    <AdvancedPDFViewer
      pdfUrl={pdfUrl}
      isMobile={isMobile}
      onProgress={handleProgressUpdate}
      onSignatureFieldDetected={handleSignatureFieldDetected}
      onSignatureClick={() => setSignatureModalOpen(true)}
      onPageChange={handlePageChange}
    />
  ), [pdfUrl, isMobile, handleProgressUpdate, handleSignatureFieldDetected, handlePageChange]);

  const signaturePanel = useMemo(() => (
    <div className="signature-form-panel">
      <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
        <div className="form-section">
          <h3>Personal Information</h3>
          
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                if (formErrors.name) {
                  formAccessibility.clearValidationMessage(e.target);
                  setFormErrors(prev => ({ ...prev, name: "" }));
                }
              }}
              className={formErrors.name ? "error" : ""}
              aria-describedby={formErrors.name ? "name-error" : undefined}
              required
            />
            {formErrors.name && (
              <div id="name-error" role="alert" className="error-message">
                {formErrors.name}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="position">Position/Title *</label>
            <input
              type="text"
              id="position"
              name="position"
              value={formData.position}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, position: e.target.value }));
                if (formErrors.position) {
                  formAccessibility.clearValidationMessage(e.target);
                  setFormErrors(prev => ({ ...prev, position: "" }));
                }
              }}
              className={formErrors.position ? "error" : ""}
              aria-describedby={formErrors.position ? "position-error" : undefined}
              required
            />
            {formErrors.position && (
              <div id="position-error" role="alert" className="error-message">
                {formErrors.position}
              </div>
            )}
          </div>

          {aplMigration && (
            <div className="form-group">
              <label htmlFor="address">Company Address *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, address: e.target.value }));
                  if (formErrors.address) {
                    formAccessibility.clearValidationMessage(e.target);
                    setFormErrors(prev => ({ ...prev, address: "" }));
                  }
                }}
                className={formErrors.address ? "error" : ""}
                aria-describedby={formErrors.address ? "address-error" : undefined}
                required
              />
              {formErrors.address && (
                <div id="address-error" role="alert" className="error-message">
                  {formErrors.address}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="signature-section">
          <h3>Digital Signature</h3>
          
          {activeSignature ? (
            <div className="signature-preview">
              <img src={activeSignature.dataUrl} alt="Your signature" />
              <button
                type="button"
                onClick={() => setSignatureModalOpen(true)}
                className="change-signature-btn"
              >
                Change Signature
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setSignatureModalOpen(true)}
              className="create-signature-btn primary"
            >
              Create Signature
            </button>
          )}
        </div>

        <div className="agreement-section">
          <div className="agreement-notice">
            <p><strong>Please read the agreement documents carefully before signing.</strong></p>
          </div>
          
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={formData.isChecked}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, isChecked: e.target.checked }));
                if (formErrors.agreement) {
                  setFormErrors(prev => ({ ...prev, agreement: "" }));
                }
              }}
              aria-describedby={formErrors.agreement ? "agreement-error" : undefined}
              required
            />
            <span className="checkmark"></span>
            <span className="agreement-text">
              I hereby agree with the terms and conditions written under agreement documents 
              and I confirm that all data contained in this document is accurate and can be accounted for.
            </span>
          </label>
          {formErrors.agreement && (
            <div id="agreement-error" role="alert" className="error-message">
              {formErrors.agreement}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!activeSignature || !formData.isChecked}
            className="submit-btn primary"
          >
            {isLoading ? "Submitting..." : "Sign & Submit Document"}
          </button>
        </div>
      </form>
    </div>
  ), [formData, formErrors, activeSignature, handleSubmit, isLoading, aplMigration]);

  const sidebarContent = useMemo(() => (
    <div className="document-sidebar">
      <div className="document-outline">
        <h4>Document Outline</h4>
        <div className="outline-content">
          <p>Pages: {totalPages}</p>
          <p>Current: {currentPage}</p>
          <p>Progress: {Math.round(documentProgress)}%</p>
        </div>
      </div>
    </div>
  ), [totalPages, currentPage, documentProgress]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className="loading-container"
        role="status"
        aria-label="Loading document"
      >
        <ReactLoading
          color="#9A2B59"
          type="bubbles"
          height={100}
          width={150}
        />
        <p>Loading document...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-container" role="alert">
        <div className="error-content">
          <h2>Error Loading Document</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-sign-page" ref={mainContainerRef}>

      {/* Responsive Layout with Enhanced Components */}
      <ResponsiveLayout
        pdfViewer={pdfViewer}
        sidebarContent={sidebarContent}
        signaturePanel={signaturePanel}
        isMobile={isMobile}
        documentProgress={documentProgress}
        onLayoutChange={(layout) => {
          // Handle layout changes if needed
        }}
      />

      {/* Multi-Modal Signature Creation */}
      <MultiModalSignature
        isOpen={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSignatureChange={handleSignatureCreated}
        initialSignature={activeSignature?.dataUrl}
      />

      {/* Accessibility: Skip link */}
      {/* <a href="#main-content" className="skip-link">
        Skip to main content
      </a> */}

      {/* Screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {/* Dynamic announcements will be inserted here */}
      </div>
    </div>
  );
}

export default SignPage;
